import React, { useState } from "react";
import {
  Close,
  CloudUpload,
  KeyboardArrowRight,
  SaveOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import LabeledField from "../../components/form/LabeledField";
import { WorkflowCanvas, WorkflowFormField } from "./workflowBuilderData";
import WorkflowDateField from "./WorkflowDateField";
import WorkflowUserPicker from "./WorkflowUserPicker";

interface WorkflowFormStageProps {
  canvas: WorkflowCanvas;
  values: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
  onSaveDraft: () => void;
  onContinue: () => void;
  /** A save or submit is in flight — both actions are disabled and Continue says so. */
  busy?: boolean;
  /** Server-side validation messages keyed by field id; they replace the field's hint. */
  fieldErrors?: Record<string, string>;
  /** Shown above the actions — a failed save, a conflict, or issues no field claims. */
  banner?: React.ReactNode;
}

/** Fields authored as hidden are revealed by a rule the engine does not have yet, so the runtime
 *  leaves them out rather than showing a control the case cannot act on. */
const isVisible = (field: WorkflowFormField) => field.visibility === "show";

const WorkflowFormStage: React.FC<WorkflowFormStageProps> = ({
  canvas,
  values,
  onChange,
  onSaveDraft,
  onContinue,
  busy = false,
  fieldErrors = {},
  banner,
}) => {
  const [showErrors, setShowErrors] = useState(false);

  const visibleFields = canvas.fields.filter(isVisible);
  const hiddenCount = canvas.fields.length - visibleFields.length;

  const missingRequired = visibleFields.filter(
    (field) => field.required && !field.readOnly && !(values[field.id] ?? "").trim(),
  );

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.name, event.target.value);
    setShowErrors(false);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    onChange(event.target.name, event.target.value);
    setShowErrors(false);
  };

  // The runtime has nowhere to put the blob yet, so the case carries the file name — enough to
  // show what was picked and to satisfy the required check.
  const handleFileChange = (fieldId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(fieldId, event.target.files?.[0]?.name ?? "");
    setShowErrors(false);
  };

  const handleContinue = () => {
    if (missingRequired.length > 0) {
      setShowErrors(true);
      return;
    }
    onContinue();
  };

  const renderField = (field: WorkflowFormField) => {
    const value = values[field.id] ?? "";
    const serverError = fieldErrors[field.id];
    const invalid = Boolean(serverError) || (showErrors && field.required && !field.readOnly && !value.trim());
    const errorText = serverError ?? (invalid ? "This field is required." : undefined);
    const hint = invalid ? undefined : field.help || undefined;
    const label = field.label || "Untitled field";

    if (field.dataType === "dropdown" || field.dataType === "boolean") {
      const options =
        field.dataType === "boolean" ? ["Yes", "No"] : field.options.filter(Boolean);

      return (
        <LabeledField
          key={field.id}
          controlId={field.id}
          label={label}
          required={field.required}
          badge={field.readOnly ? "Read-only" : undefined}
          error={errorText}
          hint={hint}
        >
          <FormControl
            size="small"
            fullWidth
            error={invalid}
            required={field.required}
          >
            <Select
              id={field.id}
              labelId={`${field.id}-label`}
              name={field.id}
              value={value}
              onChange={handleSelectChange}
              disabled={field.readOnly}
              displayEmpty
            >
              <MenuItem value="">Choose one…</MenuItem>
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </LabeledField>
      );
    }

    // A person, stored as their Portal user id - what "User from a field" and "Manager of a field
    // user" resolve from. It rendered as a plain text box before, so a typed name was saved and
    // every field-based owner, approver and task stayed unresolved (Epic 80430 QA, 2026-10-19).
    if (field.dataType === "userpicker") {
      return (
        <LabeledField
          key={field.id}
          controlId={field.id}
          label={label}
          required={field.required}
          badge={field.readOnly ? "Read-only" : undefined}
          error={errorText}
          hint={hint}
        >
          <WorkflowUserPicker
            id={field.id}
            label=""
            value={value}
            onChange={(next) => {
              onChange(field.id, next);
              setShowErrors(false);
            }}
            disabled={field.readOnly}
            error={invalid}
            placeholder="Search for a person"
          />
        </LabeledField>
      );
    }

    if (field.dataType === "date") {
      return (
        <LabeledField
          key={field.id}
          controlId={field.id}
          label={label}
          required={field.required}
          badge={field.readOnly ? "Read-only" : undefined}
          error={errorText}
          hint={hint}
        >
          <WorkflowDateField
            id={field.id}
            value={value}
            onChange={(next) => {
              onChange(field.id, next);
              setShowErrors(false);
            }}
            required={field.required}
            disabled={field.readOnly}
            error={invalid}
          />
        </LabeledField>
      );
    }

    if (field.dataType === "file") {
      return (
        <LabeledField
          key={field.id}
          controlId={field.id}
          label={label}
          required={field.required}
          badge={field.readOnly ? "Read-only" : undefined}
          error={errorText}
          hint={hint}
        >
          {/* Same control as the File Explorer's upload popup (src/features/upload/UploadUI.tsx):
              a read-only name box with the Upload button inside the trailing adornment. The real
              input stays hidden but keeps the field id, so the label still points at it. */}
          <TextField
            className="labeled-field__file-control"
            value={value}
            placeholder="No file selected"
            size="small"
            fullWidth
            error={invalid}
            disabled={field.readOnly}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {value && !field.readOnly && (
                    <IconButton
                      size="small"
                      aria-label={`Remove the file chosen for ${label}`}
                      onClick={() => onChange(field.id, "")}
                    >
                      <Close />
                    </IconButton>
                  )}
                  <Button
                    size="small"
                    component="label"
                    variant="contained"
                    color="secondary"
                    startIcon={<CloudUpload />}
                    disabled={field.readOnly}
                  >
                    Upload
                    <input
                      id={field.id}
                      name={field.id}
                      type="file"
                      hidden
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleFileChange(field.id, event)
                      }
                    />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </LabeledField>
      );
    }

    return (
      <LabeledField
        key={field.id}
        controlId={field.id}
        label={label}
        required={field.required}
        badge={field.readOnly ? "Read-only" : undefined}
        error={errorText}
        hint={hint}
      >
        <TextField
          id={field.id}
          name={field.id}
          value={value}
          onChange={handleTextChange}
          size="small"
          fullWidth
          required={field.required}
          error={invalid}
          disabled={field.readOnly}
          type={field.dataType === "number" ? "number" : "text"}
          multiline={field.dataType === "richtext"}
          minRows={field.dataType === "richtext" ? field.settings.rows || 3 : undefined}
        />
      </LabeledField>
    );
  };

  return (
    <>
      {/* Prototype's generic form starts with .sec-h using the canvas name. The canvas summary
          belongs on the Builder tile, not here — it is not shown at runtime. */}
      <Typography component="h3" className="workflow-run__section-title">
        {canvas.name}
      </Typography>

      {visibleFields.length === 0 ? (
        <Typography className="workflow-run__empty-stage">
          This canvas has no fields yet. Add them in the Builder to collect information here.
        </Typography>
      ) : (
        <Box className="workflow-run__grid">{visibleFields.map(renderField)}</Box>
      )}

      {hiddenCount > 0 && (
        <Typography className="workflow-run__hint">
          {hiddenCount} field{hiddenCount === 1 ? " is" : "s are"} hidden by default and will be
          revealed by a rule.
        </Typography>
      )}

      {showErrors && missingRequired.length > 0 && (
        <Alert severity="error" className="workflow-run__alert">
          Complete every required field before continuing.
        </Alert>
      )}

      {banner}

      <Box className="workflow-run__actions">
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<SaveOutlined />}
          onClick={onSaveDraft}
          disabled={busy}
        >
          Save draft
        </Button>
        <Box className="workflow-run__actions-spacer" />
        <Button
          variant="contained"
          endIcon={busy ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : <KeyboardArrowRight />}
          onClick={handleContinue}
          disabled={busy}
        >
          {busy ? "Saving…" : "Continue"}
        </Button>
      </Box>
    </>
  );
};

export default WorkflowFormStage;
